<template>
  <v-container fluid >
    <!-- Main content -->
    <v-card elevation="24">
      <!-- Tabs mejorados -->
      <v-tabs 
        v-model="activeTab" 
        background-color="transparent"
        color="primary"
        grow
        class="custom-tabs"
      >
        <v-tab class="custom-tab">
          <v-icon left>mdi-file-tree</v-icon>
          <span class="tab-text">Árbol de Almacenes</span>
        </v-tab>
        <v-tab class="custom-tab">
          <v-icon left>mdi-plus-circle</v-icon>
          <span class="tab-text">Nuevo Almacén</span>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab" class="tabs-content">
        <!-- Tree View Tab -->
        <v-tab-item>
          <v-card-text class="pa-6">
            <!-- Business filter mejorado -->
            <v-card class="mb-6" outlined>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" md="8">
                    <search-bussines-locations
                      :type="false"
                      @filter_loaded="changeBusiness"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <v-chip
                      v-if="lastFilter"
                      color="primary"
                      outlined
                      class="ma-1"
                    >
                      <v-icon left small>mdi-office-building</v-icon>
                      {{ treeData.length }} almacenes
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Empty state mejorado -->
            <div v-if="!lastFilter" class="empty-state">
              <div class="empty-state-content">
                <v-icon size="120" color="blue lighten-2">mdi-warehouse</v-icon>
                <h3 class="empty-title mt-4 mb-2">
                  Selecciona la unidad de negocio
                </h3>
                <p class="empty-subtitle grey--text">
                  Para comenzar a gestionar tus almacenes, selecciona una unidad de negocio en el filtro superior
                </p>
              </div>
            </div>

            <!-- Tree view mejorado con v-treeview -->
            <div v-else>
              <v-card class="tree-card" outlined>
                <!-- Header de la tree view -->
                <v-card-title class="tree-header">
                  <div class="d-flex align-center flex-grow-1">
                    <v-avatar color="primary" size="48" class="mr-3">
                      <v-icon color="white">mdi-file-tree</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="tree-title">Estructura de Almacenes</h3>
                      <p class="tree-subtitle mb-0">
                        {{ treeData.length }} almacén{{ treeData.length !== 1 ? 'es' : '' }} principal{{ treeData.length !== 1 ? 'es' : '' }}
                        <span v-if="searchTerm && searchMatchCount" class="search-results ml-2">
                          ({{ searchMatchCount }} encontrado{{ searchMatchCount !== 1 ? 's' : '' }})
                        </span>
                      </p>
                    </div>
                  </div>

                  <div class="tree-controls">
                    <!-- Search mejorado con debounce -->
                    <v-text-field
                      v-model="searchTerm"
                      prepend-inner-icon="mdi-magnify"
                      label="Buscar almacén..."
                      outlined
                      dense
                      hide-details
                      clearable
                      class="search-field mr-3"
                      @input="onSearchInput"
                      @click:clear="clearSearch"
                    >
                      <template v-slot:append-outer v-if="searchTerm && searchMatchCount">
                        <v-chip
                          small
                          color="success"
                          class="ml-2"
                        >
                          {{ searchMatchCount }} resultado{{ searchMatchCount !== 1 ? 's' : '' }}
                        </v-chip>
                      </template>
                    </v-text-field>

                    <!-- Control buttons -->
                    <div class="d-flex">
                      <v-btn 
                        small 
                        color="primary"
                        class="mr-2"
                        @click="expandAllNodes"
                        title="Expandir todo"
                        :loading="loading"
                      >
                        <v-icon small left>mdi-unfold-more-horizontal</v-icon>
                        Expandir
                      </v-btn>
                      <v-btn 
                        small 
                        color="grey darken-1"
                        dark
                        @click="collapseAllNodes"
                        title="Contraer todo"
                        :loading="loading"
                      >
                        <v-icon small left>mdi-unfold-less-horizontal</v-icon>
                        Contraer
                      </v-btn>
                    </div>

                    <!-- Add root warehouse -->
                    <v-btn
                      v-if="hasFeature('INV-092')"
                      color="primary"
                      small
                      @click="addRootWarehouse"
                      class="add-root-btn"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Nuevo Almacén Raíz
                    </v-btn>
                  </div>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Tree content -->
                <v-card-text class="pa-0">
                  <div class="tree-container" v-if="currentTreeData.length > 0">
                    <div class="tree-content pa-4">
                      <!-- Implementación con v-treeview -->
                      <v-treeview
                        ref="warehouseTree"
                        v-model="selectedNodes"
                        :active.sync="activeNodes"
                        :items="currentTreeData"
                        :open.sync="expandedNodes"
                        :search="internalSearchTerm"
                        :filter="customFilter"
                        item-key="id"
                        item-text="name"
                        item-children="children"
                        open-on-click
                        return-object
                        hoverable
                        activatable
                        dense
                        shaped
                        class="warehouse-treeview"
                        @update:active="onNodeActive"
                        @update:open="onOpenUpdate"
                      >
                        <template v-slot:prepend="{ item }">
                          <v-icon
                            :color="isNodeHighlighted(item) ? 'primary' : (item.active ? 'primary lighten-1' : 'grey')"
                            :class="{'pulse-icon': searchTerm && isNodeHighlighted(item)}"
                          >
                            {{ hasChildren(item) ? 'mdi-warehouse' : 'mdi-package-variant-closed' }}
                          </v-icon>
                        </template>
                        
                        <template v-slot:label="{ item }">
                          <div class="node-details d-flex align-center flex-grow-1">
                            <div>
                              <div class="warehouse-title" v-html="highlightSearchText(item.name)"></div>
                              <div class="warehouse-subtitle d-flex align-center">
                                <v-chip
                                  x-small
                                  :color="item.active ? 'success' : 'grey'"
                                  text-color="white"
                                  class="status-chip mr-2"
                                >
                                  {{ item.active ? 'Activo' : 'Inactivo' }}
                                </v-chip>
                                <span class="location-text" v-html="highlightSearchText(item.location_description_short || item.location_description_long || '')"></span>
                              </div>
                            </div>
                          </div>
                        </template>

                        <template v-slot:append="{ item }">
                          <div class="node-actions d-flex align-center">
                            <!-- View button -->
                            <v-btn
                              small
                              icon
                              color="info"
                              class="action-btn mr-1"
                              @click.stop="viewWarehouse(item)"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>

                            <!-- Edit button -->
                            <v-btn
                              v-if="hasFeature('INV-092')"
                              small
                              icon
                              color="warning"
                              class="action-btn mr-1"
                              @click.stop="editWarehouse(item)"
                            >
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>

                            <!-- Add child button -->
                            <v-btn
                              v-if="hasFeature('INV-092')"
                              small
                              icon
                              color="success"
                              class="action-btn mr-1"
                              @click.stop="addChildWarehouse(item)"
                            >
                              <v-icon small>mdi-folder-plus</v-icon>
                            </v-btn>

                            <!-- Status toggle -->
                            <v-switch
                              v-if="hasFeature('INV-092')"
                              v-model="item.active"
                              dense
                              hide-details
                              class="status-switch ml-1"
                              color="success"
                              @change.stop="updateWarehouseStatus(item)"
                              @click.stop
                            ></v-switch>
                          </div>
                        </template>
                      </v-treeview>
                    </div>
                  </div>

                  <!-- Empty tree state -->
                  <div v-else class="empty-tree-state">
                    <div class="empty-tree-content">
                      <v-icon size="80" color="grey lighten-1">
                        {{ searchTerm ? 'mdi-magnify-scan' : 'mdi-warehouse-off' }}
                      </v-icon>
                      <h4 class="empty-tree-title mt-4 mb-2">
                        {{ searchTerm ? 'No se encontraron almacenes' : 'No hay almacenes registrados' }}
                      </h4>
                      <p class="empty-tree-subtitle grey--text mb-4">
                        {{ searchTerm ? 'Intenta con otros términos de búsqueda' : 'Crea tu primer almacén para comenzar' }}
                      </p>
                      <v-btn
                        v-if="!searchTerm && hasFeature('INV-092')"
                        color="primary"
                        @click="addRootWarehouse"
                      >
                        <v-icon left>mdi-plus</v-icon>
                        Crear Primer Almacén
                      </v-btn>
                      <v-btn 
                        v-else
                        color="grey darken-1" 
                        text
                        @click="clearSearch"
                      >
                        <v-icon left>mdi-close</v-icon>
                        Limpiar Búsqueda
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- New Warehouse Tab -->
        <v-tab-item>
          <v-card-text class="pa-6">
            <!-- Parent warehouse alert -->
            <v-alert
              v-if="selectedParentWarehouse"
              type="info"
              outlined
              prominent
              class="mb-6 parent-alert"
            >
              <div class="d-flex align-center">
                <div class="parent-info flex-grow-1">
                  <div class="parent-title">
                    <v-icon left color="info">mdi-warehouse</v-icon>
                    <strong>Almacén padre seleccionado</strong>
                  </div>
                  <div class="parent-details mt-2">
                    <div class="parent-name">{{ selectedParentWarehouse.name }}</div>
                    <div class="parent-location caption grey--text">
                      {{ selectedParentWarehouse.bussines_unit_description }} • 
                      {{ selectedParentWarehouse.location_description_long }}
                    </div>
                  </div>
                </div>
                <v-btn 
                  icon 
                  @click="clearParentSelection"
                  class="clear-parent-btn"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-alert>

            <!-- Form -->
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-card class="form-card" outlined>
                <v-card-title class="form-header">
                  <v-icon left color="primary">mdi-warehouse</v-icon>
                  <span>{{ formTitle }}</span>
                </v-card-title>

                <v-card-text class="pt-4">
                  <v-row>
                    <!-- Business Unit -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="editedItem.business_units_id"
                        :items="itemsBussines"
                        label="Unidad de negocio"
                        :readonly="disableBusinessInput"
                        outlined
                        prepend-inner-icon="mdi-office-building"
                        :rules="businessRules"
                        @change="onBusinessChange"
                        class="custom-field"
                      >
                        <template v-slot:selection="{ item }">
                          <v-chip small color="primary" outlined>
                            {{ item.text }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Location -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="editedItem.location_id"
                        :items="itemsLocations2"
                        label="Ubicación"
                        :readonly="disableLocationInput"
                        outlined
                        prepend-inner-icon="mdi-map-marker"
                        :rules="locationRules"
                        class="custom-field"
                      >
                        <template v-slot:selection="{ item }">
                          <v-chip small color="secondary" outlined>
                            {{ item.text }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Warehouse Name -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedItem.name"
                        label="Nombre del almacén"
                        outlined
                        prepend-inner-icon="mdi-warehouse"
                        :rules="nameRules"
                        class="custom-field"
                      ></v-text-field>
                    </v-col>

                    <!-- Address -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedItem.address"
                        label="Dirección"
                        outlined
                        prepend-inner-icon="mdi-map-marker-outline"
                        class="custom-field"
                      ></v-text-field>
                    </v-col>

                    <!-- Description -->
                    <v-col cols="12">
                      <v-textarea
                        v-model="editedItem.note"
                        label="Descripción / Notas"
                        outlined
                        rows="3"
                        prepend-inner-icon="mdi-note-text"
                        class="custom-field"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-card-text>

                <!-- Form actions -->
                <v-card-actions class="form-actions">
                  <v-spacer></v-spacer>
                  <v-btn 
                    text 
                    color="grey darken-1" 
                    @click="resetForm"
                    class="cancel-btn"
                  >
                    <v-icon left>mdi-close</v-icon>
                    Cancelar
                  </v-btn>
                  <v-btn 
                    color="primary" 
                    @click="saveWarehouse" 
                    :disabled="!valid" 
                    :loading="saving"
                    class="save-btn"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ editedIndex === -1 ? 'Crear Almacén' : 'Actualizar Almacén' }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Edit Dialog mejorado -->
    <v-dialog v-model="editDialog" max-width="600px" persistent>
      <v-card class="edit-dialog">
        <v-card-title class="edit-header">
          <v-icon left color="white">mdi-pencil</v-icon>
          <span>{{ formTitle }}</span>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="editForm" v-model="editValid" lazy-validation>
            <v-text-field
              v-model="editedItem.name"
              label="Nombre del almacén"
              outlined
              prepend-inner-icon="mdi-warehouse"
              :rules="nameRules"
              class="custom-field"
            ></v-text-field>

            <v-text-field
              v-model="editedItem.address"
              label="Dirección"
              outlined
              prepend-inner-icon="mdi-map-marker-outline"
              class="custom-field"
            ></v-text-field>

            <v-textarea
              v-model="editedItem.note"
              label="Descripción / Notas"
              outlined
              rows="3"
              prepend-inner-icon="mdi-note-text"
              class="custom-field"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions class="edit-actions">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="closeEditDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="saveEditedWarehouse" :disabled="!editValid" :loading="saving">
            <v-icon left>mdi-content-save</v-icon>
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

export default {
  data: () => ({
    activeTab: 0,
    searchTerm: '',
    internalSearchTerm: '', // Para el v-treeview
    searchTimeout: null,
    searchMatchCount: 0,
    valid: true,
    editValid: true,
    editDialog: false,
    saving: false,
    loading: false,
    disableBusinessInput: false,
    disableLocationInput: false,
    lastFilter: null,
    selectedParentWarehouse: null,
    expandedNodes: [],
    activeNodes: [],
    selectedNodes: [],
    
    // Estado para búsqueda
    isSearchActive: false,
    savedExpandedNodes: [],

    // Data
    treeData: [],
    itemsBussines: [],
    itemsLocations: [],
    itemsLocations2: [],

    // Form data
    editedIndex: -1,
    editedItem: {
      id: null,
      name: '',
      address: '',
      note: '',
      business_units_id: null,
      location_id: null,
      parent_warehouse_id: null
    },
    defaultItem: {
      id: null,
      name: '',
      address: '',
      note: '',
      business_units_id: null,
      location_id: null,
      parent_warehouse_id: null
    }
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Nuevo Almacén' : 'Editar Almacén';
    },
    
    // Validation rules
    businessRules() {
      return [v => !!v || 'La unidad de negocio es requerida'];
    },
    
    locationRules() {
      return [v => !!v || 'La ubicación es requerida'];
    },
    
    nameRules() {
      return [
        v => !!v || 'El nombre es requerido',
      ];
    },

    // Datos del árbol que se muestran actualmente
    currentTreeData() {
      return this.treeData;
    },

    // Custom filter para el v-treeview
    customFilter() {
      return (item, search, textKey) => {
        if (!search) return true;
        
        const searchLower = search.toLowerCase();
        
        // Buscar en nombre
        if (item.name && item.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        // Buscar en dirección
        if (item.address && item.address.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        // Buscar en ubicación
        if (item.location_description_short && item.location_description_short.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        if (item.location_description_long && item.location_description_long.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        return false;
      };
    }
  },

  watch: {
    editDialog(val) {
      if (!val) this.closeEditDialog();
    },
    
    // Watcher para manejar la búsqueda
    searchTerm(newVal, oldVal) {
      // Limpiar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Si hay texto, activar búsqueda con debounce
      if (newVal && newVal.trim()) {
        this.searchTimeout = setTimeout(() => {
          this.activateSearch();
          this.internalSearchTerm = newVal;
          this.updateSearchMatchCount();
        }, 300);
      } else {
        // Si se borra, desactivar búsqueda inmediatamente
        this.deactivateSearch();
        this.internalSearchTerm = '';
        this.searchMatchCount = 0;
      }
    }
  },

  mounted() {
    this.initialize();
    this.expandedNodes = [];
  },

  methods: {
    // ================================
    // MÉTODOS DE BÚSQUEDA MEJORADOS
    // ================================
    
    /**
     * Maneja el input de búsqueda
     */
    onSearchInput(value) {
      // El watcher de searchTerm se encarga del resto
    },
    
    /**
     * Activa el modo de búsqueda y expande nodos
     */
    activateSearch() {
      if (!this.isSearchActive) {
        // Guardar estado actual
        this.savedExpandedNodes = [...this.expandedNodes];
        this.isSearchActive = true;
        
        // Expandir todos los nodos para mostrar resultados
        this.$nextTick(() => {
          this.expandAllNodesForSearch();
        });
      }
    },
    
    /**
     * Desactiva el modo de búsqueda
     */
    deactivateSearch() {
      if (this.isSearchActive) {
        this.isSearchActive = false;
        
        // Restaurar estado previo
        this.$nextTick(() => {
          this.expandedNodes = [...this.savedExpandedNodes];
          this.savedExpandedNodes = [];
        });
      }
    },
    
    /**
     * Expande todos los nodos durante la búsqueda
     */
    expandAllNodesForSearch() {
      const getAllNodeIds = (nodes) => {
        let ids = [];
        nodes.forEach(node => {
          if (this.hasChildren(node)) {
            ids.push(node.id);
            if (node.children && node.children.length > 0) {
              ids = ids.concat(getAllNodeIds(node.children));
            }
          }
        });
        return ids;
      };
      
      const allNodeIds = getAllNodeIds(this.treeData);
      this.expandedNodes = allNodeIds;
      
      // Forzar actualización del treeview
      const treeView = this.$refs.warehouseTree;
      if (treeView && treeView.updateAll) {
        treeView.updateAll(true);
      }
    },
    
    /**
     * Limpia la búsqueda
     */
    clearSearch() {
      this.searchTerm = '';
      this.deactivateSearch();
    },
    
    /**
     * Cuenta los resultados de búsqueda
     */
    updateSearchMatchCount() {
      if (!this.searchTerm) {
        this.searchMatchCount = 0;
        return;
      }
      
      const countMatches = (nodes) => {
        let count = 0;
        nodes.forEach(node => {
          if (this.customFilter(node, this.searchTerm)) {
            count++;
          }
          if (node.children && node.children.length > 0) {
            count += countMatches(node.children);
          }
        });
        return count;
      };
      
      this.searchMatchCount = countMatches(this.treeData);
    },
    
    /**
     * Resalta el texto de búsqueda
     */
    highlightSearchText(text) {
      if (!text || !this.searchTerm) {
        return text || '';
      }
      
      const searchTerm = this.searchTerm.toLowerCase();
      const textLower = text.toLowerCase();
      
      if (!textLower.includes(searchTerm)) {
        return text;
      }
      
      const regex = new RegExp(`(${this.escapeRegex(this.searchTerm)})`, 'gi');
      return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    },
    
    /**
     * Escapa caracteres especiales para regex
     */
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    // ================================
    // MÉTODOS ORIGINALES RESTAURADOS
    // ================================
    
    // Utility methods for tree
    hasChildren(node) {
      return (
        (node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0) ||
        (node.children_count && node.children_count > 0)
      );
    },

    isNodeHighlighted(node) {
      if (!this.searchTerm || !node) return false;
      return this.customFilter(node, this.searchTerm);
    },

    // Controlador de eventos para actualizaciones de nodos expandidos
    onOpenUpdate(openNodes) {
      if (!this.isSearchActive) {
        this.expandedNodes = openNodes;
      }
    },

    // Método directo para expandir todos los nodos - ORIGINAL RESTAURADO
    expandAllNodes() {
      this.loading = true;
      
      try {
        // 1. Primero hacemos una lista plana de todos los nodos
        const getAllNodes = (nodes, result = []) => {
          if (!nodes || !nodes.length) return result;
          
          nodes.forEach(node => {
            result.push(node);
            if (node.children && node.children.length > 0) {
              getAllNodes(node.children, result);
            }
          });
          
          return result;
        };
        
        // 2. Obtenemos todos los nodos en formato plano
        const allNodesFlat = getAllNodes(this.currentTreeData);
        
        // 3. Filtramos solo los nodos que son padres (tienen hijos)
        const parentNodes = allNodesFlat.filter(node => node.children && node.children.length > 0);
        
        // 4. Extraemos solo los IDs
        const parentIds = parentNodes.map(node => node.id);
        
        
        // 5. Limpiar y luego aplicar con un pequeño retraso 
        this.expandedNodes = [];
        
        // 6. Aplicar expansión
        setTimeout(() => {
          this.expandedNodes = parentIds;
          
          // 7. Obtener acceso directo al componente Vuetify
          const treeComponent = this.$refs.warehouseTree;
          if (treeComponent) {
            // 8. Forzar actualización visual
            treeComponent.$forceUpdate();
            
            // 9. Si hay métodos internos de Vuetify, intentar usarlos
            if (treeComponent.updateAll) {
              treeComponent.updateAll(true);
            }
          }
          
          this.loading = false;
        }, 200);
      } catch (error) {
        console.error('Error al expandir nodos:', error);
        this.loading = false;
      }
    },
    
    // Método para colapsar todos los nodos - ORIGINAL RESTAURADO
    collapseAllNodes() {
      this.loading = true;
      
      try {
        // Si estamos en búsqueda, salir primero
        if (this.isSearchActive) {
          this.clearSearch();
        }
        
        // Vaciar completamente el array de nodos expandidos
        this.expandedNodes = [];
        
        // Accedemos al componente v-treeview y forzamos su actualización
        const treeView = this.$refs.warehouseTree;
        if (treeView) {
          setTimeout(() => {
            treeView.$forceUpdate();
            this.loading = false;
          }, 100);
        } else {
          this.loading = false;
        }
      } catch (error) {
        console.error('Error al colapsar el árbol:', error);
        this.loading = false;
      }
    },
    
    onNodeActive(activeNodes) {
      this.activeNode = activeNodes.length > 0 ? activeNodes[0] : null;
    },

    // Load warehouses from API
    async loadWarehouses(filter) {
      try {
        let filters = ` WHERE w.BUSINESS_UNITS_ID = ${filter.business}`;
        if (filter.location != 0 && filter.location != null) {
          filters += ` AND w.LOCATION_ID = ${filter.location}`;
        }

        const data = { filters };
        const response = await this.api_Post("inventory/warehouse/tree", data);

        if (response.status === 200) {
          const result = await response.json();
          this.treeData = this.buildTree(result);
          
          // Limpiar búsqueda al cargar nuevos datos
          this.clearSearch();
        } else {
          this.$toast.error("Error al cargar los almacenes");
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
        this.$toast.error("Error al cargar los almacenes");
      }
    },

    // Build tree structure
    buildTree(items) {
      const itemMap = {};
      const roots = [];

      // Create item map
      items.forEach(item => {
        itemMap[item.id] = { ...item, children: [] };
      });

      // Build tree
      items.forEach(item => {
        if (item.parent_warehouse_id === null) {
          roots.push(itemMap[item.id]);
        } else if (itemMap[item.parent_warehouse_id]) {
          itemMap[item.parent_warehouse_id].children.push(itemMap[item.id]);
        }
      });

      return roots;
    },

    initialize() {
      this.itemsBussines = Object.assign([], this.$store.state.auth.bussines_units);
      this.itemsLocations = this.$store.state.auth.locations;
    },

    // Business filter change
    async changeBusiness(filter) {
      this.lastFilter = filter;
      if (filter.business == 0) {
        this.treeData = [];
        this.clearSearch();
        return;
      }
      await this.loadWarehouses(filter);
    },

    // Warehouse operations
    addRootWarehouse() {
      this.selectedParentWarehouse = null;
      this.editedItem = { ...this.defaultItem };
      this.disableBusinessInput = false;
      this.disableLocationInput = false;
      this.editedIndex = -1;
      this.activeTab = 1;
    },

    addChildWarehouse(parentNode) {
      this.selectedParentWarehouse = parentNode;
      this.editedItem = {
        ...this.defaultItem,
        business_units_id: parentNode.business_units_id,
        location_id: parentNode.location_id,
        parent_warehouse_id: parentNode.id
      };

      this.disableBusinessInput = true;
      this.disableLocationInput = true;
      this.editedIndex = -1;

      this.updateLocationCombo(parentNode.business_units_id);
      this.activeTab = 1;
    },

    editWarehouse(node) {
      this.editedItem = { 
        ...node, 
        parent_warehouse_id: node.parent_warehouse_id || node.parent 
      };
      this.editedIndex = 1;
      this.editDialog = true;
    },

    viewWarehouse(node) {
      this.$toast.info(`Viendo detalles de: ${node.name}`);
    },

    async updateWarehouseStatus(item) {
      try {
        const data = {
          id: item.id,
          active: item.active
        };

        const response = await this.api_Post("inventory/warehouse/updateState", data);

        if (response.status === 200) {
          this.$toast.success("Estado actualizado con éxito");
          this.updateNodeInTree(this.treeData, item.id, { active: item.active });
        } else {
          this.$toast.error("Error al actualizar el estado");
        }
      } catch (error) {
        console.error('Error updating status:', error);
        this.$toast.error("Error al actualizar el estado");
      }
    },

    updateNodeInTree(nodes, nodeId, updates) {
      for (let node of nodes) {
        if (node.id === nodeId) {
          Object.assign(node, updates);
          return true;
        }
        if (node.children && this.updateNodeInTree(node.children, nodeId, updates)) {
          return true;
        }
      }
      return false;
    },

    // Form operations
    onBusinessChange() {
      this.updateLocationCombo(this.editedItem.business_units_id);
      this.editedItem.location_id = null;
    },

    updateLocationCombo(businessId) {
      this.itemsLocations2 = this.itemsLocations
        .filter(location => location.bus_id === businessId)
        .map(location => ({
          value: location.value,
          text: location.text
        }));
    },

    clearParentSelection() {
      this.selectedParentWarehouse = null;
      this.disableBusinessInput = false;
      this.disableLocationInput = false;
      this.editedItem.parent_warehouse_id = null;
      this.editedItem.business_units_id = null;
      this.editedItem.location_id = null;
      this.itemsLocations2 = [];
    },

    async saveWarehouse() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;

      try {
        const data = {
          id: this.editedItem.id,
          parent_warehouse_id: this.editedItem.parent_warehouse_id,
          business_units_id: this.editedItem.business_units_id,
          location_id: this.editedItem.location_id,
          name: this.editedItem.name,
          address: this.editedItem.address || '',
          note: this.editedItem.note || '',
          image_url: ''
        };

        const endpoint = 'inventory/warehouse';
        const method = this.editedIndex === -1 ? 'api_Post' : 'api_Put';

        const response = await this[method](endpoint, data);

        if (response.status === 200) {
          this.$toast.success(
            this.editedIndex === -1 ?
              'Almacén creado con éxito' :
              'Almacén actualizado con éxito'
          );

          this.resetForm();
          await this.loadWarehouses(this.lastFilter);
          this.activeTab = 0;
        } else {
          const result = await response.json();
          this.$toast.error(result.error || 'Error al guardar el almacén');
        }
      } catch (error) {
        console.error('Error saving warehouse:', error);
        this.$toast.error('Error al guardar el almacén');
      } finally {
        this.saving = false;
      }
    },

    async saveEditedWarehouse() {
      if (!this.$refs.editForm.validate()) return;

      this.saving = true;

      try {
        const data = {
          id: this.editedItem.id,
          parent_warehouse_id: this.editedItem.parent_warehouse_id,
          business_units_id: this.editedItem.business_units_id,
          location_id: this.editedItem.location_id,
          name: this.editedItem.name,
          address: this.editedItem.address || '',
          note: this.editedItem.note || '',
          image_url: this.editedItem.image_url || ''
        };

        const response = await this.api_Put('inventory/warehouse', data);

        if (response.status === 200) {
          this.$toast.success('Almacén actualizado con éxito');
          this.closeEditDialog();
          await this.loadWarehouses(this.lastFilter);
        } else {
          const result = await response.json();
          this.$toast.error(result.error || 'Error al actualizar el almacén');
        }
      } catch (error) {
        console.error('Error updating warehouse:', error);
        this.$toast.error('Error al actualizar el almacén');
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
      this.clearParentSelection();
    },

    closeEditDialog() {
      this.editDialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    }
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap");

.v-application {
  font-family: "Roboto", sans-serif;
}

/* ================================
   ESTILOS DE BÚSQUEDA MEJORADOS
   ================================ */

.search-field {
  max-width: 350px;
  min-width: 250px;
  transition: all 0.3s ease;
}

.search-field.v-input--is-focused {
  transform: scale(1.02);
}

.search-results {
  color: var(--v-success-base);
  font-weight: 500;
  animation: fadeIn 0.3s ease;
}

.search-highlight {
  background: linear-gradient(135deg, #fff59d 0%, #ffeb3b 100%);
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
  color: #333;
  box-shadow: 0 1px 3px rgba(255, 235, 59, 0.4);
}

/* Animación para resultados de búsqueda */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ================================
   ESTILOS DEL TREEVIEW MEJORADOS
   ================================ */

.warehouse-treeview.v-treeview {
  min-height: 300px;
}

.warehouse-treeview.v-treeview .v-treeview-node__root {
  padding: 6px 12px;
  margin-bottom: 4px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-left: 3px solid transparent;
  position: relative;
}

.warehouse-treeview.v-treeview .v-treeview-node__root:hover {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.12) 100%);
  border-left-color: var(--v-primary-base);
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
}

.warehouse-treeview.v-treeview .v-treeview-node--active > .v-treeview-node__root {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.2) 100%);
  border-left-color: var(--v-primary-base);
  border-left-width: 4px;
}

.warehouse-treeview.v-treeview .warehouse-title {
  font-weight: 500;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.warehouse-treeview.v-treeview .warehouse-subtitle {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

.warehouse-treeview.v-treeview .location-text {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.warehouse-treeview.v-treeview .action-btn {
  margin: 0 2px;
  transition: all 0.2s ease;
}

.warehouse-treeview.v-treeview .action-btn:hover {
  transform: scale(1.1);
}

.warehouse-treeview.v-treeview .status-switch {
  margin-left: 8px;
}

/* Tree Container */
.tree-container {
  max-height: 70vh;
  overflow-y: auto;
  background: white;
}

.tree-container::-webkit-scrollbar {
  width: 8px;
}

.tree-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.tree-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
  transition: background 0.3s ease;
}

.tree-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Tree Content */
.tree-content {
  padding: 16px;
}

.tree-card {
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.3s ease;
}

.tree-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.tree-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 24px;
  transition: all 0.3s ease;
}

.tree-title {
  color: var(--v-secondary-base);
  font-weight: 500;
  font-size: 1.3rem;
  margin: 0;
}

.tree-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 4px;
}

.tree-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.add-root-btn {
  text-transform: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.add-root-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

/* Status Chip */
.status-chip {
  flex-shrink: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 16px !important;
  transition: all 0.3s ease;
}

/* Node Actions */
.node-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.7;
  transition: opacity 0.3s ease;
  flex-shrink: 0;
}

.warehouse-treeview .v-treeview-node__root:hover .node-actions {
  opacity: 1;
}

.node-actions .v-btn {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.node-actions .v-btn:hover {
  transform: scale(1.15) translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.status-switch {
  max-width: 52px;
}

.status-switch .v-input--selection-controls__input {
  margin-right: 0;
}

/* Empty Tree State */
.empty-tree-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px;
}

.empty-tree-content {
  text-align: center;
  max-width: 400px;
}

.empty-tree-title {
  color: var(--v-secondary-base);
  font-weight: 400;
  margin-bottom: 8px;
}

.empty-tree-subtitle {
  font-size: 14px;
  line-height: 1.5;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-title {
  color: var(--v-secondary-base);
  font-weight: 400;
}

.empty-subtitle {
  font-size: 16px;
  line-height: 1.5;
}

/* Parent Alert */
.parent-alert {
  border-radius: 12px !important;
  border-left: 6px solid var(--v-info-base) !important;
  transition: all 0.3s ease;
}

.parent-alert:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.parent-info {
  padding: 8px 0;
}

.parent-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  margin-bottom: 8px;
}

.parent-name {
  font-size: 18px;
  font-weight: 500;
  color: var(--v-secondary-base);
}

.parent-location {
  margin-top: 4px;
}

.clear-parent-btn {
  transition: transform 0.2s ease;
}

.clear-parent-btn:hover {
  transform: scale(1.1) rotate(90deg);
}

/* Form Card */
.form-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.3s ease;
}

.form-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.form-header {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-lighten1) 100%);
  color: white;
  padding: 20px 24px;
}

.form-header .v-icon {
  margin-right: 12px;
}

.custom-field {
  margin-bottom: 8px;
}

.custom-field .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.custom-field.v-input--is-focused .v-input__slot {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

.form-actions {
  padding: 20px 24px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  text-transform: none;
  font-weight: 500;
  margin-right: 12px;
  transition: all 0.3s ease;
}

.save-btn {
  text-transform: none;
  font-weight: 500;
  border-radius: 8px;
  padding: 0 24px;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

/* Edit Dialog */
.edit-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.edit-header {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-lighten1) 100%);
  color: white;
  padding: 20px 24px;
}

.edit-actions {
  padding: 16px 24px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

/* Pulse animation for search highlights */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.pulse-icon {
  animation: pulse 1.5s infinite;
  color: var(--v-primary-base) !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .tree-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .search-field {
    max-width: none;
    min-width: auto;
  }
  
  .tree-header {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .tree-header {
    padding: 12px;
  }
  
  .warehouse-treeview.v-treeview .v-treeview-node__root {
    padding: 8px 12px;
  }
  
  .warehouse-title {
    font-size: 1rem;
  }
  
  .node-actions {
    gap: 4px;
  }
  
  .custom-tab {
    padding: 16px 20px;
    font-size: 14px;
  }
  
  .tab-text {
    display: none;
  }
}

/* Animations */
@keyframes slideInUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.tree-card {
  animation: slideInUp 0.6s ease-out;
}

.warehouse-treeview .v-treeview-node__root {
  animation: slideInUp 0.3s ease-out;
}

/* Hover effects */
.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

.v-card:hover {
  transform: translateY(-2px);
  transition: transform 0.3s ease;
}

/* Focus states */
.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

.v-autocomplete--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
}

/* Tooltips */
.v-tooltip__content {
  background: rgba(0, 0, 0, 0.9) !important;
  border-radius: 8px !important;
  font-size: 12px !important;
}

/* Tabs */
.custom-tabs {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.custom-tab {
  text-transform: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.custom-tab:hover {
  background: rgba(25, 118, 210, 0.1);
}

.tab-text {
  margin-left: 8px;
}
</style>